import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { Loader2, Save, Mail, Pause, Play, ScrollText, AlertTriangle, Send } from 'lucide-react';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function formatTimestamp(iso) {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  } catch { return iso.slice(0, 16); }
}

export function CaptureSettingsTab() {
  const { api } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);

  // Send log dialog
  const [logOpen, setLogOpen] = useState(false);
  const [logEntries, setLogEntries] = useState([]);
  const [logLoading, setLogLoading] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadConfig = async () => {
    try {
      const res = await api.get('/settings/capture_config');
      setConfig(res.data);
    } catch {
      toast.error('Failed to load capture settings');
    } finally {
      setLoading(false);
    }
  };

  const loadLog = async () => {
    setLogLoading(true);
    try {
      const res = await api.get('/captures/send-log?limit=100');
      setLogEntries(res.data.entries || []);
    } catch {
      toast.error('Failed to load send log');
    } finally {
      setLogLoading(false);
    }
  };

  const openLog = () => {
    setLogOpen(true);
    loadLog();
  };

  const handleEmailChange = (key, field, value) => {
    setConfig(prev => ({
      ...prev,
      destinations: {
        ...prev.destinations,
        [key]: { ...prev.destinations[key], [field]: value },
      },
    }));
  };

  const sendTest = async (key) => {
    setTesting(key);
    try {
      const res = await api.post('/settings/capture_config/test-send', { destination: key });
      toast.success(`Test email sent to ${res.data.sent_to}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test send failed');
    } finally {
      setTesting(null);
    }
  };

  const handleSave = async () => {
    // Validate primary + cc emails on each destination
    const dests = config.destinations || {};
    for (const [key, dest] of Object.entries(dests)) {
      if (dest.primary_email && !EMAIL_RE.test(dest.primary_email)) {
        toast.error(`Invalid Primary Email for ${key.replace('_', ' ')}`);
        return;
      }
      if (dest.cc_email && !EMAIL_RE.test(dest.cc_email)) {
        toast.error(`Invalid CC Email for ${key.replace('_', ' ')}`);
        return;
      }
    }
    // Validate global BCC if populated
    const bcc = (config.global_bcc || '').trim();
    if (bcc && !EMAIL_RE.test(bcc)) {
      toast.error('Invalid Global BCC email format');
      return;
    }

    setSaving(true);
    try {
      const res = await api.put('/settings/capture_config', config);
      setConfig(res.data);
      toast.success('Capture settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save capture settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-[#F5A623]" />
      </div>
    );
  }
  if (!config) return null;

  const destCards = [
    { key: 'livecosts_dev', label: 'LiveCosts', company: 'Lux Developments', color: 'border-blue-500/30' },
    { key: 'livecosts_con', label: 'LiveCosts', company: 'Lux Construction', color: 'border-blue-500/30' },
    { key: 'xero_dev',      label: 'Xero',      company: 'Lux Developments', color: 'border-green-500/30' },
    { key: 'xero_con',      label: 'Xero',      company: 'Lux Construction', color: 'border-green-500/30' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Capture Email Routing</h3>
          <p className="text-sm text-gray-400">Configure where receipt captures are emailed for each company</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={openLog}
            variant="outline"
            className="border-white/20 text-gray-300 hover:bg-white/5"
            data-testid="view-send-log-btn"
          >
            <ScrollText className="w-4 h-4 mr-2" />
            View Send Log
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]"
            data-testid="save-capture-config"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Settings
          </Button>
        </div>
      </div>

      {/* Sending-not-configured warning */}
      {config.sending_configured === false && (
        <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-sm" data-testid="resend-not-configured-warning">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium">Resend API key not configured</p>
            <p className="text-amber-200/70 text-xs mt-0.5">Capture emails will not be sent until <code className="bg-black/20 px-1 rounded">RESEND_API_KEY</code> is set in the backend environment.</p>
          </div>
        </div>
      )}

      {/* Pause Toggle */}
      <Card className="bg-[#2A3240] border-white/10">
        <CardContent className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            {config.sending_paused ? (
              <Pause className="w-5 h-5 text-amber-400" />
            ) : (
              <Play className="w-5 h-5 text-green-400" />
            )}
            <div>
              <p className="text-white font-medium">
                {config.sending_paused ? 'Sending Paused' : 'Sending Active'}
              </p>
              <p className="text-xs text-gray-400">
                {config.sending_paused
                  ? 'Captures will be saved but NOT emailed until resumed'
                  : 'Captures are automatically emailed after upload'}
              </p>
            </div>
          </div>
          <Switch
            checked={!config.sending_paused}
            onCheckedChange={(checked) => setConfig(prev => ({ ...prev, sending_paused: !checked }))}
            data-testid="sending-paused-toggle"
          />
        </CardContent>
      </Card>

      {/* Global BCC — full-width, above the 4 destination cards */}
      <Card className="bg-[#2A3240] border-white/10">
        <CardContent className="p-4 space-y-2">
          <Label className="text-sm text-white">Global BCC <span className="text-gray-500 font-normal">(optional)</span></Label>
          <Input
            type="email"
            value={config.global_bcc || ''}
            onChange={(e) => setConfig(prev => ({ ...prev, global_bcc: e.target.value }))}
            placeholder="audit@luxdev.app"
            className="bg-[#1E2530] border-white/20 text-white"
            data-testid="global-bcc-input"
          />
          <p className="text-xs text-gray-400">All capture emails will blind-copy this address. Useful for audit/backup.</p>
        </CardContent>
      </Card>

      {/* Destination Cards */}
      <div className="text-sm text-gray-400 -mb-2" data-testid="dest-routing-hint">
        Posted Cost Inbox invoices are emailed to every destination switched <span className="text-white">on</span> below (per company). Xero uses its “email your bills to” address. Save your changes before sending a test.
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {destCards.map(({ key, label, company, color }) => {
          const dest = config.destinations?.[key] || { primary_email: '', cc_email: '' };
          const enabled = dest.enabled !== undefined
            ? dest.enabled
            : (key.startsWith('livecosts') && EMAIL_RE.test(dest.primary_email || ''));
          return (
            <Card key={key} className={`bg-[#2A3240] ${color} border ${enabled ? '' : 'opacity-70'}`} data-testid={`dest-card-${key}`}>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm text-white flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-400" />
                    {label} — {company}
                  </span>
                  <span className="flex items-center gap-2 text-xs font-normal text-gray-400">
                    {enabled ? 'On' : 'Off'}
                    <Switch
                      checked={enabled}
                      onCheckedChange={(checked) => handleEmailChange(key, 'enabled', checked)}
                      data-testid={`${key}-enabled-toggle`}
                    />
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs text-gray-400">Primary Email</Label>
                  <Input
                    value={dest.primary_email || ''}
                    onChange={(e) => handleEmailChange(key, 'primary_email', e.target.value)}
                    placeholder="invoices@example.com"
                    className="bg-[#1E2530] border-white/20 text-white text-sm"
                    data-testid={`${key}-primary-email`}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-gray-400">CC Email (optional)</Label>
                  <Input
                    value={dest.cc_email || ''}
                    onChange={(e) => handleEmailChange(key, 'cc_email', e.target.value || null)}
                    placeholder="accounts@example.com"
                    className="bg-[#1E2530] border-white/20 text-white text-sm"
                    data-testid={`${key}-cc-email`}
                  />
                </div>
                <div className="flex justify-end pt-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => sendTest(key)}
                    disabled={testing === key || !EMAIL_RE.test(dest.primary_email || '')}
                    title={EMAIL_RE.test(dest.primary_email || '') ? 'Sends a test email to the saved address' : 'Add and save a Primary Email first'}
                    className="border-white/20 text-gray-300 hover:bg-white/5"
                    data-testid={`${key}-send-test`}
                  >
                    {testing === key ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                    Send test
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Send Log Dialog */}
      <Dialog open={logOpen} onOpenChange={setLogOpen}>
        <DialogContent className="bg-[#2A3240] border-white/10 text-white max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <ScrollText className="w-5 h-5 text-[#F5A623]" /> Capture Send Log
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto -mx-6 px-6">
            {logLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#F5A623]" /></div>
            ) : logEntries.length === 0 ? (
              <p className="text-gray-400 text-center py-8" data-testid="send-log-empty">No send-log entries yet.</p>
            ) : (
              <div className="space-y-2" data-testid="send-log-list">
                {logEntries.map(e => (
                  <div key={e.id} className="p-3 rounded bg-[#1E2530] border border-white/5 text-sm">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={
                        e.status === 'sent'
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-red-500/20 text-red-400'
                      }>
                        {e.status}
                      </Badge>
                      <span className="text-white font-medium">Job {e.job_reference}</span>
                      <span className="text-gray-500">→</span>
                      <span className="text-gray-300">{e.destination}</span>
                      <span className="text-gray-500 text-xs ml-auto">{formatTimestamp(e.created_at)}</span>
                    </div>
                    <div className="mt-1 text-xs text-gray-400 flex gap-3 flex-wrap">
                      <span>To: {e.primary_email}</span>
                      {e.cc_email && <span>CC: {e.cc_email}</span>}
                      {e.bcc_email && <span>BCC: {e.bcc_email}</span>}
                    </div>
                    {e.message_id && (
                      <p className="mt-1 text-xs text-gray-500 font-mono truncate">Resend ID: {e.message_id}</p>
                    )}
                    {e.error && (
                      <p className="mt-1 text-xs text-red-400 break-all">Error: {e.error}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
